import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { inviteSiteAdmin } from '../../services/api/facilityService';
import { EMAIL_REGEX } from '../../utils/emailUtils';

interface InviteSiteAdminDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    departmentId: string;
    facilityName: string;
    onInvited: () => void;
}

export default function InviteSiteAdminDialog({
    open,
    onOpenChange,
    departmentId,
    facilityName,
    onInvited,
}: InviteSiteAdminDialogProps) {
    const { toast } = useToast();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmStep, setConfirmStep] = useState(false);

    useEffect(() => {
        if (open) {
            setEmail('');
            setConfirmStep(false);
        }
    }, [open]);

    const handleNext = () => {
        if (!EMAIL_REGEX.test(email.trim())) {
            toast({
                title: 'Invalid email',
                description: 'Please enter a valid email address.',
                variant: 'destructive',
            });
            return;
        }
        setConfirmStep(true);
    };

    const handleSend = async () => {
        setLoading(true);
        try {
            await inviteSiteAdmin(departmentId, [email.trim()]);
            toast({
                title: 'Invite sent',
                description: `Invitation sent to ${email}`,
            });
            onOpenChange(false);
            onInvited();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to send invitation.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Invite Site Admin</DialogTitle>
                    <DialogDescription>
                        Invite a Site Admin for {facilityName}
                    </DialogDescription>
                </DialogHeader>

                {!confirmStep ? (
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="admin-email">Email address</Label>
                            <Input
                                id="admin-email"
                                type="email"
                                placeholder="admin@hospital.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleNext}>Next</Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        <div className="rounded-md border p-4 space-y-2">
                            <p className="text-sm">
                                <span className="text-muted-foreground">
                                    Email:
                                </span>{' '}
                                <span className="font-medium">{email}</span>
                            </p>
                            <p className="text-sm text-muted-foreground">
                                This will send a Site Admin invitation to join{' '}
                                {facilityName}.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setConfirmStep(false)}
                            >
                                Back
                            </Button>
                            <Button onClick={handleSend} disabled={loading}>
                                {loading ? 'Sending...' : 'Send Invite'}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
